import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from '@shared/core';
import { Flex, SpacingStack, TextV2 } from '@withjoy/joykit';
import { PhotoAdjustDialogLayout } from '../PhotoAdjustDialogLayout/PhotoAdjustDialogLayout';
import { PhotoAlignmentDialogProps, PhotoAlignmentDialogRootProps, PhotoAlignmentProps } from './PhotoAlignmentDialog.types';
import { FocusPoint } from '../../../FocusPoint/FocusPoint';
import { PhotoXPosition, PhotoYPosition } from '@graphql/generated';
import type { PhotoAlignmentX, PhotoAlignmentY, WebsitePhotoAlignment } from '@shared/types/WebsiteHeaderSettings.types';

export const PhotoAlignment: FC<PhotoAlignmentProps> = props => {
  const { imageData, focusPoint, handleFocusPointChange } = props;

  const { tq } = useTranslation('multiPhotoSelector');
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      gap={6}
    >
      <FocusPoint
        imageData={imageData}
        focusPoint={focusPoint}
        onFocusPointChange={handleFocusPointChange}
      />

      <TextV2
        typographyVariant="label2"
        textAlign="center"
        color="mono10"
      >
        {tq`Focus point ensures your image displays well on all devices.`}
      </TextV2>
    </Flex>
  );
};

const Root: FC<PhotoAlignmentDialogRootProps> = props => {
  const { onClose, imageData, alignment = { alignX: PhotoXPosition.center, alignY: PhotoYPosition.center }, handleSave } = props;

  const [focusPoint, setFocusPoint] = useState<WebsitePhotoAlignment>({ alignX: alignment.alignX, alignY: alignment?.alignY || PhotoYPosition.center });

  const { tq } = useTranslation('multiPhotoSelector');

  const handleFocusPointChange = useCallback((alignX: PhotoAlignmentX, alignY: PhotoAlignmentY) => {
    setFocusPoint({ alignX, alignY });
  }, []);

  return (
    <>
      <SpacingStack
        spacing={7}
        width="100%"
      >
        <PhotoAdjustDialogLayout.Title
          title={tq`Focus Point`}
          onClose={onClose}
        />

        <PhotoAlignment
          focusPoint={focusPoint}
          handleFocusPointChange={handleFocusPointChange}
          imageData={imageData}
        />
      </SpacingStack>
      <PhotoAdjustDialogLayout.ActionButtons
        handleSave={() => handleSave(focusPoint)}
        onClose={onClose}
      />
    </>
  );
};

export const PhotoAlignmentDialog: FC<PhotoAlignmentDialogProps> = props => {
  const { isOpen, onClose, handleSave, alignment, imageData } = props;

  return (
    <PhotoAdjustDialogLayout
      isOpen={isOpen}
      onClose={onClose}
    >
      {imageData && (
        <Root
          imageData={imageData}
          alignment={alignment}
          handleSave={handleSave}
          onClose={onClose}
        />
      )}
    </PhotoAdjustDialogLayout>
  );
};

PhotoAlignmentDialog.displayName = 'PhotoAlignmentDialog';
